import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  Users,
  Zap,
  ArrowUpRight,
  Bot,
  ShieldCheck,
  Pause,
  Play,
  X,
  Sparkles,
} from "lucide-react";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import { Sparkline } from "../components/dashboard/Charts";
import CopyModal from "../components/dashboard/CopyModal";
import { traders, riskBadge } from "../copyTradingMock";
import { useCopies } from "../hooks/useCopies";

const formatMoney = (n) =>
  n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const compactNum = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
};

const TraderCard = ({ trader, onCopy }) => {
  const rb = riskBadge(trader.risk);
  const isAI = trader.type === "AI Bot";
  return (
    <div className="bb-card bb-card-hover rounded-2xl overflow-hidden relative group">
      <div
        className="absolute -top-12 -right-12 w-44 h-44 rounded-full blur-3xl opacity-40 group-hover:opacity-70 transition-opacity"
        style={{ background: trader.color }}
      ></div>
      <div className="relative p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center text-[14px] font-bold text-white shrink-0"
              style={{ background: `linear-gradient(135deg, ${trader.color}, ${trader.accent})` }}
            >
              {trader.avatar}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-display font-bold text-[16px] text-white truncate">{trader.name}</span>
                {isAI ? (
                  <Bot size={13} className="text-[#7d8eff] shrink-0" />
                ) : (
                  <ShieldCheck size={13} className="text-emerald-400 shrink-0" />
                )}
              </div>
              <div className="text-[11px] text-white/45 truncate">{trader.handle}</div>
            </div>
          </div>
          <div
            className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${rb.color} ${rb.bg} ${rb.border}`}
          >
            {trader.risk}
          </div>
        </div>

        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[11px] text-white/55 mb-0.5">Total return</div>
            <div className="font-display font-bold text-[28px] text-emerald-400 leading-none tabular-nums">
              +{trader.totalReturn}%
            </div>
            <div className="text-[11px] text-white/45 mt-1">{trader.age} · {trader.monthlyReturn}%/mo</div>
          </div>
          <div className="opacity-90">
            <Sparkline data={trader.equity} color="#22c55e" width={120} height={40} />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
          <div className="py-2 rounded-lg bg-white/[0.02] border border-white/5">
            <div className="text-[10px] text-white/45">Win rate</div>
            <div className="text-[13px] text-white font-medium tabular-nums">{trader.winRate}%</div>
          </div>
          <div className="py-2 rounded-lg bg-white/[0.02] border border-white/5">
            <div className="text-[10px] text-white/45">Max DD</div>
            <div className="text-[13px] text-white font-medium tabular-nums">{trader.maxDrawdown}%</div>
          </div>
          <div className="py-2 rounded-lg bg-white/[0.02] border border-white/5">
            <div className="text-[10px] text-white/45">Copiers</div>
            <div className="text-[13px] text-white font-medium tabular-nums">{compactNum(trader.followers)}</div>
          </div>
        </div>

        <div className="flex gap-2">
          <Link
            to={`/dashboard/copy-trading/${trader.id}`}
            className="flex-1 py-2.5 rounded-lg border border-white/10 hover:border-white/25 text-white text-[13px] font-medium text-center transition-all"
          >
            View profile
          </Link>
          <button
            onClick={() => onCopy(trader)}
            className="flex-1 py-2.5 rounded-lg bb-blue text-white text-[13px] font-medium inline-flex items-center justify-center gap-1.5 transition-all"
          >
            <Zap size={13} /> Copy
          </button>
        </div>
      </div>
    </div>
  );
};

const MyCopyRow = ({ copy, onPause, onStop }) => {
  const pnl = copy.currentValue - copy.allocated;
  const ret = (pnl / copy.allocated) * 100;
  const profit = pnl >= 0;
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/5 hover:border-white/10 hover:bg-white/[0.02] transition-colors">
      <Link to={`/dashboard/copy-trading/${copy.traderId}`} className="flex items-center gap-3 flex-1 min-w-0">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-[12px] font-bold text-white shrink-0"
          style={{ background: copy.traderColor }}
        >
          {copy.traderAvatar}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[13px] text-white font-medium truncate">{copy.traderName}</span>
            {copy.status === "paused" && (
              <span className="text-[9px] uppercase tracking-wider text-amber-400 font-semibold px-1.5 py-0.5 rounded bg-amber-400/10">
                Paused
              </span>
            )}
          </div>
          <div className="text-[11px] text-white/45 tabular-nums">
            ${formatMoney(copy.allocated)} @ {copy.multiplier}x
          </div>
        </div>
      </Link>
      <div className="text-right shrink-0">
        <div className="text-[13px] text-white tabular-nums font-medium">${formatMoney(copy.currentValue)}</div>
        <div
          className={`text-[11px] tabular-nums font-medium ${
            profit ? "text-emerald-400" : "text-rose-400"
          }`}
        >
          {profit ? "+" : ""}${formatMoney(pnl)} ({profit ? "+" : ""}{ret.toFixed(2)}%)
        </div>
      </div>
      <div className="flex gap-1.5 shrink-0">
        <button
          onClick={() => onPause(copy.id)}
          className="w-8 h-8 rounded-lg border border-white/10 hover:border-white/25 text-white/55 hover:text-white flex items-center justify-center transition-all"
          title={copy.status === "active" ? "Pause" : "Resume"}
        >
          {copy.status === "active" ? <Pause size={13} /> : <Play size={13} />}
        </button>
        <button
          onClick={() => onStop(copy.id)}
          className="w-8 h-8 rounded-lg border border-white/10 hover:border-rose-400 hover:bg-rose-500/10 text-white/55 hover:text-rose-400 flex items-center justify-center transition-all"
          title="Stop copying"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
};

const CopyTrading = () => {
  const [filter, setFilter] = useState("All");
  const [sort, setSort] = useState("return");
  const [modalTrader, setModalTrader] = useState(null);
  const { copies, pauseCopy, stopCopy, totalAllocated, totalValue, totalPnL, totalReturnPct } =
    useCopies();

  const filtered = useMemo(() => {
    let list = traders.slice();
    if (filter === "AI Bots") list = list.filter((t) => t.type === "AI Bot");
    if (filter === "Humans") list = list.filter((t) => t.type === "Human");
    if (sort === "return") list.sort((a, b) => b.totalReturn - a.totalReturn);
    if (sort === "followers") list.sort((a, b) => b.followers - a.followers);
    if (sort === "risk") list.sort((a, b) => a.riskScore - b.riskScore);
    if (sort === "winrate") list.sort((a, b) => b.winRate - a.winRate);
    return list;
  }, [filter, sort]);

  return (
    <DashboardLayout title="Copy Trading" subtitle="Mirror top traders">
      {/* My copies */}
      {copies.length > 0 && (
        <div className="bb-card rounded-2xl p-5 lg:p-6 mb-5">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={14} className="text-[#7d8eff]" />
                <span className="text-[12px] uppercase tracking-wider text-white/55 font-semibold">
                  My copies
                </span>
              </div>
              <div className="font-display font-bold text-[24px] text-white tabular-nums">
                ${formatMoney(totalValue)}
              </div>
              <div
                className={`text-[13px] font-medium tabular-nums mt-0.5 ${
                  totalPnL >= 0 ? "text-emerald-400" : "text-rose-400"
                }`}
              >
                {totalPnL >= 0 ? "+" : ""}${formatMoney(totalPnL)} ({totalPnL >= 0 ? "+" : ""}
                {totalReturnPct.toFixed(2)}%)
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-right">
              <div>
                <div className="text-[11px] text-white/45">Allocated</div>
                <div className="text-[15px] text-white font-medium tabular-nums">
                  ${formatMoney(totalAllocated)}
                </div>
              </div>
              <div>
                <div className="text-[11px] text-white/45">Traders</div>
                <div className="text-[15px] text-white font-medium tabular-nums">{copies.length}</div>
              </div>
              <div className="hidden sm:block">
                <div className="text-[11px] text-white/45">Status</div>
                <div className="text-[15px] text-emerald-400 font-medium">Active</div>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {copies.map((c) => (
              <MyCopyRow key={c.id} copy={c} onPause={pauseCopy} onStop={stopCopy} />
            ))}
          </div>
        </div>
      )}

      {/* Hero strip when empty */}
      {copies.length === 0 && (
        <div className="bb-card rounded-2xl p-6 lg:p-8 mb-5 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-gradient-to-br from-[#1c3df5]/30 to-transparent blur-3xl"></div>
          <div className="relative max-w-2xl">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-3 bg-[#1c3df5]/15 border border-[#1c3df5]/30 rounded-full text-[10px] uppercase tracking-wider font-semibold text-[#7d8eff]">
              <Sparkles size={11} /> NEW
            </div>
            <h2 className="font-display font-bold text-[26px] lg:text-[34px] text-white leading-tight mb-2">
              Trade like the best.<br />Automatically.
            </h2>
            <p className="text-white/55 text-[14px] leading-relaxed max-w-lg">
              Pick from our top performing AI bots and verified human traders. Their trades mirror to your account in real time. Pause, stop, or adjust risk anytime.
            </p>
            <div className="flex items-center gap-5 mt-5 text-[12px]">
              <div className="flex items-center gap-1.5 text-white/70">
                <TrendingUp size={14} className="text-emerald-400" /> Up to 200% historical return
              </div>
              <div className="flex items-center gap-1.5 text-white/70">
                <Users size={14} className="text-[#7d8eff]" /> 37,000+ active copiers
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-1 border border-white/8">
          {["All", "AI Bots", "Humans"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-[12px] rounded-md transition-colors whitespace-nowrap ${
                filter === f ? "bg-white text-[#07080d] font-medium" : "text-white/55 hover:text-white"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-white/45">Sort by</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="h-9 px-3 bg-white/[0.04] border border-white/8 rounded-lg text-[13px] text-white outline-none focus:border-[#1c3df5]"
          >
            <option value="return">Highest return</option>
            <option value="followers">Most followers</option>
            <option value="winrate">Best win rate</option>
            <option value="risk">Lowest risk</option>
          </select>
        </div>
      </div>

      {/* Traders grid */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((t) => (
          <TraderCard key={t.id} trader={t} onCopy={setModalTrader} />
        ))}
      </div>

      <div className="mt-8 bb-card rounded-2xl p-5 lg:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
          <ArrowUpRight size={20} className="text-amber-400" />
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold text-[15px] text-white mb-1">
            Past performance does not guarantee future results
          </div>
          <div className="text-[12px] text-white/55 leading-relaxed">
            Copy trading involves risk. Returns are historical and may not be repeated. You can lose more than your initial allocation. Demo mode — no real funds are moved.
          </div>
        </div>
      </div>

      <CopyModal
        trader={modalTrader}
        open={!!modalTrader}
        onClose={() => setModalTrader(null)}
      />
    </DashboardLayout>
  );
};

export default CopyTrading;
